export interface LatLng {
  lat: number;
  lng: number;
}

const R = 6_371_000;
const rad = (d: number) => (d * Math.PI) / 180;

export function distanceMeters(a: LatLng, b: LatLng): number {
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export function bearingDeg(a: LatLng, b: LatLng): number {
  const y = Math.sin(rad(b.lng - a.lng)) * Math.cos(rad(b.lat));
  const x = Math.cos(rad(a.lat)) * Math.sin(rad(b.lat)) - Math.sin(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.cos(rad(b.lng - a.lng));
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/** Local approximate ETA: city average speed, no paid routing API. */
export function etaMinutes(from: LatLng, to: LatLng, avgKmh = 25): number {
  const km = (distanceMeters(from, to) * 1.3) / 1000; // 1.3 = street detour factor
  return Math.max(1, Math.round((km / avgKmh) * 60));
}

/**
 * Demo route between two points that follows a city grid (two L-shaped legs),
 * sampled every ~stepMeters. Gives a road-like path without any routing service.
 */
export function buildDemoRoute(from: LatLng, to: LatLng, stepMeters = 60): LatLng[] {
  const mid1: LatLng = { lat: from.lat + (to.lat - from.lat) * 0.45, lng: from.lng };
  const mid2: LatLng = { lat: mid1.lat, lng: to.lng };
  const legs: [LatLng, LatLng][] = [
    [from, mid1],
    [mid1, mid2],
    [mid2, to],
  ];
  const pts: LatLng[] = [from];
  for (const [a, b] of legs) {
    const d = distanceMeters(a, b);
    const n = Math.max(1, Math.ceil(d / stepMeters));
    for (let i = 1; i <= n; i++) {
      pts.push({ lat: a.lat + ((b.lat - a.lat) * i) / n, lng: a.lng + ((b.lng - a.lng) * i) / n });
    }
  }
  return pts;
}

/** Deterministic point near a center, from a string (used to place an address with no geocoder). */
export function pseudoGeocode(center: LatLng, text: string, maxKm = 4): LatLng {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const u = ((h >>> 0) % 10_000) / 10_000;
  const v = (((h >>> 0) / 10_000) % 10_000) / 10_000;
  const km = 1 + u * (maxKm - 1);
  const angle = v * 2 * Math.PI;
  return {
    lat: center.lat + (km / 111.32) * Math.sin(angle),
    lng: center.lng + (km / (111.32 * Math.cos(rad(center.lat)))) * Math.cos(angle),
  };
}
